/**
 * Follow-up Service - API client for follow-up operations
 */

import api from "../../../shared/api.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Create a new follow-up
 * @param {Object} followUpData - Follow-up data
 * @returns {Promise<Object>} Created follow-up
 */
export const createFollowUp = async (followUpData) => {
  try {
    const response = await api.post("/followups", followUpData);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      // If backend returns success/data structure
      if (response.data.success !== undefined) {
        return response.data;
      }
      // If backend returns data directly, wrap it
      return {
        success: true,
        data: response.data,
      };
    }
    // Fallback
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error creating follow-up", {
      followUpData,
      error: error.message,
      status: error.response?.status,
    });
    // Return error response instead of throwing
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to create follow-up",
      data: null,
    };
  }
};

/**
 * Get follow-ups with filters
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Follow-ups data
 */
export const getFollowUps = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      // Only include filters that have a value (exclude null, undefined, empty string)
      // This ensures "All" selections (null) are not sent to backend
      const value = filters[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const url = queryString ? `/followups?${queryString}` : "/followups";
    const response = await api.get(url);
    
    // Ensure we always return a consistent structure
    if (response.data && typeof response.data === 'object') {
      // If backend returns success/data structure
      if (response.data.success !== undefined) {
        return response.data;
      }
      // If backend returns data directly
      return {
        success: true,
        data: Array.isArray(response.data) ? response.data : (response.data.data || []),
        count: response.data.count || (Array.isArray(response.data) ? response.data.length : 0),
      };
    }
    
    // Fallback
    return {
      success: true,
      data: [],
      count: 0,
    };
  } catch (error) {
    logError("Error fetching follow-ups", {
      filters,
      error: error.message,
      status: error.response?.status,
    });
    // Check if it's a network/connection error vs API error
    const errorMessage = error.response?.data?.error || error.message || "Failed to fetch follow-ups";
    
    // If it's a 404 or "not found", treat as empty result
    if (error.response?.status === 404 || errorMessage.toLowerCase().includes("not found")) {
      return {
        success: true,
        data: [],
        count: 0,
      };
    }
    
    // Return error response for other errors
    return {
      success: false,
      error: errorMessage,
      data: [],
      count: 0,
    };
  }
};

/**
 * Get follow-up by ID
 * @param {string} followUpId - Follow-up ID
 * @returns {Promise<Object>} Follow-up data
 */
export const getFollowUpById = async (followUpId) => {
  try {
    const response = await api.get(`/followups/${followUpId}`);
    // Ensure consistent response structure
    if (response.data && response.data.success !== undefined) {
      return response.data;
    }
    // If backend returns data directly
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error fetching follow-up", {
      followUpId,
      error: error.message,
      status: error.response?.status,
    });
    // Return error response instead of throwing
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to fetch follow-up",
      data: null,
    };
  }
};

/**
 * Update follow-up
 * @param {string} followUpId - Follow-up ID
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Updated follow-up
 */
export const updateFollowUp = async (followUpId, updateData) => {
  try {
    const response = await api.put(`/followups/${followUpId}`, updateData);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      // If backend returns success/data structure
      if (response.data.success !== undefined) {
        return response.data;
      }
      // If backend returns data directly, wrap it
      return {
        success: true,
        data: response.data,
      };
    }
    // Fallback
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error updating follow-up", {
      followUpId,
      updateData,
      error: error.message,
      status: error.response?.status,
    });
    // Return error response instead of throwing
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to update follow-up",
      data: null,
    };
  }
};

/**
 * Complete follow-up
 * @param {string} followUpId - Follow-up ID
 * @param {string} [followUpNotes] - Notes when completing
 * @returns {Promise<Object>} Completed follow-up
 */
export const completeFollowUp = async (followUpId, followUpNotes = "") => {
  try {
    const response = await api.post(`/followups/${followUpId}/complete`, {
      followUpNotes,
    });
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data,
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error completing follow-up", {
      followUpId,
      followUpNotes,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to complete follow-up",
      data: null,
    };
  }
};

/**
 * Reschedule follow-up
 * @param {string} followUpId - Follow-up ID
 * @param {Date|string} dueDate - New due date
 * @param {Date|string} [scheduledDate] - New scheduled date
 * @returns {Promise<Object>} Rescheduled follow-up
 */
export const rescheduleFollowUp = async (
  followUpId,
  dueDate,
  scheduledDate = null
) => {
  try {
    const response = await api.post(`/followups/${followUpId}/reschedule`, {
      dueDate,
      scheduledDate,
    });
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data,
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error rescheduling follow-up", {
      followUpId,
      dueDate,
      scheduledDate,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to reschedule follow-up",
      data: null,
    };
  }
};

/**
 * Delete (cancel) follow-up
 * @param {string} followUpId - Follow-up ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteFollowUp = async (followUpId) => {
  try {
    const response = await api.delete(`/followups/${followUpId}`);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data,
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error deleting follow-up", {
      followUpId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to delete follow-up",
      data: null,
    };
  }
};

/**
 * Get today's follow-ups
 * @param {string} [assignedTo] - Filter by assigned user
 * @returns {Promise<Object>} Today's follow-ups
 */
export const getTodaysFollowUps = async (assignedTo = null) => {
  try {
    const params = assignedTo ? `?assignedTo=${assignedTo}` : "";
    const response = await api.get(`/followups/today${params}`);
    // Ensure consistent response structure
    if (response.data && typeof response.data === 'object') {
      if (response.data.success !== undefined) {
        return response.data;
      }
      return {
        success: true,
        data: response.data,
      };
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Error fetching today's follow-ups", {
      assignedTo,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.response?.data?.error || error.message || "Failed to fetch today's follow-ups",
      data: null,
    };
  }
};

